// Realtime notes for the overview tab — a PORT of the business logic in
// src/components/_dashboard/rentalToSale/useSellerNotes.ts (per the CRM revamp
// rule: reuse the logic, not the legacy component/hook), extended with:
//   - the new `pinned` flag on quick-composer writes,
//   - mirroring every update into the shared sessionCache key `notes:{id}`
//     (newest-first) so the badge/preload/notes-tab all read one source.
// Preserved legacy invariants:
//   - own-doc lookup by (userId, contactId) query — legacy docs have
//     auto-generated ids, so `.doc(deterministicId)` alone would miss them;
//   - org-shared teammates' notes via the doc-level organizationIds
//     array-contains listener, filtered per-note, read-only;
//   - doc-level organizationIds is the union of per-note orgIds and is
//     REMOVED (never `[]`) when nothing is shared;
//   - full-document set() writes (last-write-wins, mutators touch own notes only).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { getCached, setCached } from "../../data/sessionCache";
import { notesCacheKey, parseNoteTime, sortNotesNewestFirst } from "./overviewData";
import { USER_NOTES_COLLECTION_NAME, userNotesDocId } from "../../../../../../@types/sharedSchema";
import type { UserNote, UserNotesDocument } from "../../../../../../@types/sharedSchema";
import type { DisplayUserNote } from "hooks/useListingNotes";
import { withDefaultNoteShare } from "utils/noteSharing";

type UseOverviewNotesReturn = {
  notes: DisplayUserNote[];
  isLoading: boolean;
  /** Rejects on save failure (composer shows the snackbar). */
  addNote: (text: string, options?: { pinned?: boolean }) => Promise<void>;
};

// Union of per-note org ids; undefined (== field removed) when nothing shared.
function computeDocOrgIds(notes: UserNote[]): string[] | undefined {
  const set = new Set<string>();
  for (const note of notes) {
    for (const orgId of note.organizationIds ?? []) set.add(orgId);
  }
  return set.size > 0 ? Array.from(set) : undefined;
}

export function useOverviewNotes(contactId: string | undefined): UseOverviewNotesReturn {
  const {
    user: { email: userEmail }
  } = useAuth();
  const { organizationId, organizations, organizationUsers } = useFirestore();

  // Warm start: the preload already put the (own) notes into the shared cache.
  const cachedNotes = contactId
    ? getCached<DisplayUserNote[]>(notesCacheKey(contactId))
    : undefined;

  const [myNotes, setMyNotes] = useState<UserNote[]>([]);
  const [orgNotes, setOrgNotes] = useState<DisplayUserNote[]>([]);
  const [listenerFired, setListenerFired] = useState(false);
  const docIdRef = useRef<string | null>(null);
  // True only after a SUCCESSFUL own-notes snapshot (the listener's error path
  // flips listenerFired for the loading UI but does NOT hydrate). saveMyNotes
  // is a full-doc set() built from myNotes — writing before hydration would
  // rewrite the doc from [] and destroy every existing note, so addNote falls
  // back to a one-shot read when this is still false.
  const hydratedRef = useRef(false);
  // Guards the async hydration fallback against a mid-await contact switch.
  const activeContactIdRef = useRef(contactId);

  // Reset per-contact state when the page is reused for another contact.
  useEffect(() => {
    activeContactIdRef.current = contactId;
    setMyNotes([]);
    setOrgNotes([]);
    setListenerFired(false);
    docIdRef.current = null;
    hydratedRef.current = false;
  }, [contactId]);

  // Listener 1 — own notes doc.
  useEffect(() => {
    if (!contactId || !userEmail) return undefined;
    const unsubscribe = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .where("userId", "==", userEmail)
      .where("contactId", "==", contactId)
      .limit(1)
      .onSnapshot(
        (snapshot) => {
          if (!snapshot.empty) {
            const doc = snapshot.docs[0];
            const data = doc.data() as UserNotesDocument;
            docIdRef.current = doc.id;
            setMyNotes(
              (data.notes ?? []).map((note) => ({ ...note, noteTime: parseNoteTime(note.noteTime) }))
            );
          } else {
            docIdRef.current = null;
            setMyNotes([]);
          }
          hydratedRef.current = true;
          setListenerFired(true);
        },
        (error) => {
          console.error("useOverviewNotes: own-notes listener failed", error);
          setListenerFired(true);
        }
      );
    return unsubscribe;
  }, [contactId, userEmail]);

  // Listener 2 — teammates' docs with notes shared to my org for this contact.
  useEffect(() => {
    if (!contactId || !userEmail || !organizationId) return undefined;
    const unsubscribe = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .where("contactId", "==", contactId)
      .where("organizationIds", "array-contains", organizationId)
      .onSnapshot(
        (snapshot) => {
          const collected: DisplayUserNote[] = [];
          snapshot.docs.forEach((doc) => {
            const data = doc.data() as UserNotesDocument;
            if (data.userId === userEmail) return; // own doc handled by listener 1
            for (const note of data.notes ?? []) {
              // Doc-level organizationIds is a union — re-filter per note.
              if (!(note.organizationIds ?? []).includes(organizationId)) continue;
              collected.push({
                ...note,
                noteTime: parseNoteTime(note.noteTime),
                isMine: false,
                authorEmail: data.userId
              });
            }
          });
          setOrgNotes(collected);
        },
        (error) => {
          console.error("useOverviewNotes: org-notes listener failed", error);
        }
      );
    return unsubscribe;
  }, [contactId, userEmail, organizationId]);

  // Merge + resolve author names, newest-first (the shared cache contract).
  const notes = useMemo<DisplayUserNote[]>(() => {
    const nameByEmail = new Map<string, string>();
    for (const orgUser of organizationUsers ?? []) {
      if (orgUser.email) nameByEmail.set(orgUser.email, orgUser.name || orgUser.email);
    }
    const mine: DisplayUserNote[] = myNotes.map((note) => ({
      ...note,
      isMine: true,
      authorEmail: userEmail ?? ""
    }));
    const merged = sortNotesNewestFirst([...mine, ...orgNotes]);
    return merged.map((note) =>
      note.isMine ? note : { ...note, authorName: nameByEmail.get(note.authorEmail) ?? note.authorEmail }
    );
  }, [myNotes, orgNotes, organizationUsers, userEmail]);

  // Mirror the live merged list into the shared cache (badge + notes tab +
  // preload all read this key).
  useEffect(() => {
    if (!contactId || !listenerFired) return;
    setCached(notesCacheKey(contactId), notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, listenerFired, notes]);

  const saveMyNotes = useCallback(
    (updatedNotes: UserNote[]): Promise<void> => {
      if (!contactId || !userEmail) return Promise.resolve();
      const docOrgIds = computeDocOrgIds(updatedNotes);
      const docData: UserNotesDocument = {
        userId: userEmail,
        contactId,
        notes: updatedNotes,
        // Conditional include — set() without merge drops the field when absent.
        ...(docOrgIds ? { organizationIds: docOrgIds } : {})
      };
      // Existing doc (any id — legacy auto ids included) → overwrite it;
      // otherwise create with the deterministic id the backend also uses.
      const docId = docIdRef.current ?? userNotesDocId({ userId: userEmail, contactId });
      docIdRef.current = docId;
      return db.collection(USER_NOTES_COLLECTION_NAME).doc(docId).set(docData);
    },
    [contactId, userEmail]
  );

  const addNote = useCallback(
    async (text: string, options?: { pinned?: boolean }): Promise<void> => {
      const trimmed = text.trim();
      if (!trimmed || !contactId || !userEmail) return;

      // Hydration guard: before the listener's first successful snapshot,
      // myNotes is [] and a full-doc set() from it would wipe the existing doc
      // (slow network, or the listener errored). Seed from a one-shot read
      // first; if even that fails, reject — the composer restores the text.
      let baseNotes = myNotes;
      if (!hydratedRef.current) {
        const snapshot = await db
          .collection(USER_NOTES_COLLECTION_NAME)
          .where("userId", "==", userEmail)
          .where("contactId", "==", contactId)
          .limit(1)
          .get();
        if (activeContactIdRef.current !== contactId) return; // page moved on
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          docIdRef.current = doc.id;
          baseNotes = ((doc.data() as UserNotesDocument).notes ?? []).map((note) => ({
            ...note,
            noteTime: parseNoteTime(note.noteTime)
          }));
        } else {
          baseNotes = [];
        }
        hydratedRef.current = true;
      }

      // Shared with the author's offices by default (`utils/noteSharing.ts`).
      const newNote = withDefaultNoteShare(
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          note: trimmed,
          noteTime: new Date(),
          ...(options?.pinned ? { pinned: true } : {})
        },
        organizations
      );
      const updated = [newNote, ...baseNotes];
      // Optimistic: local state (and via the mirror effect, the shared cache)
      // updates immediately; rolled back if the write fails.
      setMyNotes(updated);
      return saveMyNotes(updated).catch((error) => {
        console.error("useOverviewNotes: failed saving note", error);
        setMyNotes(baseNotes);
        throw error;
      });
    },
    [contactId, userEmail, myNotes, organizations, saveMyNotes]
  );

  // Before the first listener tick, serve the preloaded cache (no flash).
  const effectiveNotes = listenerFired ? notes : cachedNotes ?? notes;

  return {
    notes: effectiveNotes,
    isLoading: !listenerFired && cachedNotes === undefined,
    addNote
  };
}
